/*
 * <smk-buy-box> — buy box B3. Markup and every number: snippets/buy-box.liquid.
 *
 * Counts BAGS per flavour (one tap, one bag of that flavour's smallest sellable size)
 * and prices the whole box the way checkout will:
 *
 *   list     = sum of bags x variant price                       (one-time)
 *   plan     = sum of bags x the bag's price on the SKIO plan    (the 9% plan price,
 *                                                                  rounded per unit)
 *   volume % = the Skio volume discount whose trigger `plan` reaches
 *              ($75 / $95 / $125 / $150 -> 4 / 7 / 10 / 13, whole numbers)
 *   sub      = round(plan x (100 - volume) / 100)
 *
 * The two discounts multiply (Skio stacks the volume % on the plan price), so the
 * progress bar and "Save N%" read the merchant's labels (9 / 12 / 15 / 18 / 20), never
 * a number above the real discount, while every $ amount is the exact checkout figure.
 *
 * W4 (welcome offer) is a mode: with the welcome code on the cart (class is-welcome,
 * set by Liquid or by checkWelcome() from /cart.js) the subscription price is
 *
 *   welcome  = plan - floor(plan x welcome % / 100)
 *
 * - the code is an order discount and Shopify rounds what it takes off DOWN ($109.16
 * at 24% -> $26.19 off -> $82.97). No volume tier on welcome orders (they don't combine);
 * "Every order after" shows the tier price the renewals will get.
 *
 * It also owns the add to cart: Dawn's product form posts one variant, a box is many
 * lines, so the form's submit is taken over in the capture phase on <product-form> and
 * posted through the same /cart/add + sections + renderContents path Dawn uses, so the
 * cart drawer opens and refreshes exactly as it does for any other add. Hosted outside
 * the PDP (no <product-form>), it takes the plain form's submit instead; with
 * data-apply-welcome a subscription first puts the welcome code on the cart, keeping
 * any codes already there (cart-gift.js then adds the free Creatine).
 */

if (!customElements.get('smk-buy-box')) {
  class SmkBuyBox extends HTMLElement {
    connectedCallback() {
      /* Moved or re-inserted: disconnectedCallback took the page-level listeners off. */
      if (this.ready) {
        this.listen(true);
        return;
      }
      const dataEl = this.querySelector('[data-bb-data]');
      if (!dataEl) return;
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (error) {
        console.error('smk-buy-box: bad data', error);
        return;
      }
      this.ready = true;

      const d = this.data;
      const rows = Array.from(this.querySelectorAll('[data-bb-row]'));
      this.bags = (d.bags || []).map((bag, i) => {
        const row = rows[i];
        const bySetting = bag.size ? Math.floor((d.maxServings || 300) / bag.size) : 0;
        return Object.assign({}, bag, {
          row,
          qty: 0,
          max: bag.cap >= 0 ? Math.min(bag.cap, bySetting) : bySetting,
        });
      });

      this.plan = 'sub';
      this.planId = this.dataset.planId || '';
      this.hiddenInput = this.querySelector('input[name="selling_plan"]');
      this.live = this.querySelector('[data-bb-live]');

      /* The flavour the page landed on starts with one bag, so the first paint shows
         a real price rather than an empty box. */
      const start = this.bags.find((b) => (b.ids || []).includes(d.selected) && b.max > 0);
      if (start) start.qty = 1;

      this.welcome = this.classList.contains('is-welcome');
      this.layout();

      this.addEventListener('click', (event) => this.onClick(event));
      this.bindTips();
      this.bindSubmit();
      this.render();
      if (!this.welcome && d.welcomeCode) this.checkWelcome();
    }

    /* "Try it once" as a card (v2) or the link under the button (alt). The theme setting
       decides; an A/B test overrides it per visitor with <html data-bb-once-layout="link|card">
       (set before this script runs) or ?bb_once=link|card. */
    layout() {
      const pick = (value) => (value === 'card' || value === 'link' ? value : null);
      let param = null;
      try {
        param = new URLSearchParams(window.location.search).get('bb_once');
      } catch (error) {
        param = null;
      }
      const layout = pick(param) || pick(document.documentElement.dataset.bbOnceLayout) || pick(this.dataset.onceLayout) || 'card';
      this.cards = layout === 'card';
      this.classList.toggle('is-cards', this.cards);
    }

    /* W4: a visitor from the welcome link carries the code on a cart Liquid saw as empty.
       The box stays invisible (is-checking) until /cart.js answers, 1.5s at most. */
    checkWelcome() {
      const done = () => this.classList.remove('is-checking');
      const timer = setTimeout(done, 1500);
      const code = String(this.data.welcomeCode).toUpperCase();
      fetch((window.routes?.cart_url || '/cart') + '.js', { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          const has = (cart.discount_codes || []).some((c) => String(c.code).toUpperCase() === code);
          if (!has) return;
          this.welcome = true;
          this.classList.add('is-welcome');
          this.render();
        })
        .catch(() => {})
        .finally(() => {
          clearTimeout(timer);
          done();
        });
    }

    disconnectedCallback() {
      this.listen(false);
    }

    /* Listeners outside the box: Dawn's form submit, and closing the tooltip. */
    listen(on) {
      const method = on ? 'addEventListener' : 'removeEventListener';
      if (this.submitHost && this.onSubmit) this.submitHost[method]('submit', this.onSubmit, true);
      if (this.onDocDown) document[method]('pointerdown', this.onDocDown);
      if (this.onKeydown) document[method]('keydown', this.onKeydown);
    }

    /* Maths --------------------------------------------------------------- */

    volumeFor(planCents) {
      const { triggers = [], volume = [] } = this.data;
      let index = -1;
      triggers.forEach((trigger, i) => {
        if (planCents >= trigger) index = i;
      });
      return { index, pct: index >= 0 ? volume[index] || 0 : 0 };
    }

    totals() {
      let servings = 0;
      let list = 0;
      let plan = 0;
      let subBlocked = false;
      this.bags.forEach((bag) => {
        if (!bag.qty) return;
        servings += bag.qty * bag.size;
        list += bag.qty * bag.price;
        if (bag.sub == null) subBlocked = true;
        else plan += bag.qty * bag.sub;
      });
      const tier = this.volumeFor(plan);
      const sub = Math.round((plan * (100 - tier.pct)) / 100);
      const labels = this.data.labels || [];
      const label = labels[tier.index + 1] != null ? labels[tier.index + 1] : labels[0];
      const welcome = plan - Math.floor((plan * (this.data.welcomePct || 0)) / 100 + 1e-9);
      /* What the welcome order takes off the one-time price, whole %, never rounded up. */
      const welcomeLabel = list > 0 ? Math.floor(100 - (welcome * 100) / list + 1e-9) : 30;

      /* "Add 1 more bag: every order after drops to N% off" - the cheapest bag that can
         still be added, and whether it reaches the next tier. */
      let nextLabel = null;
      const addable = this.bags.filter((b) => b.sub != null && b.qty < b.max).map((b) => b.sub);
      if (addable.length) {
        const next = this.volumeFor(plan + Math.min(...addable));
        if (next.index > tier.index && labels[next.index + 1] != null) nextLabel = labels[next.index + 1];
      }

      return { servings, list, plan, sub, tier, label, welcome, welcomeLabel, nextLabel, subBlocked, has: servings > 0 };
    }

    /* Steppers ------------------------------------------------------------ */

    onClick(event) {
      const step = event.target.closest('[data-bb-step]');
      if (step && this.contains(step)) {
        const row = step.closest('[data-bb-row]');
        const bag = this.bags.find((b) => b.row === row);
        if (!bag || step.disabled) return;
        const direction = Number(step.dataset.bbStep);
        const next = Math.min(Math.max(bag.qty + direction, 0), bag.max);
        if (next === bag.qty) return;
        bag.qty = next;
        if (direction > 0) {
          document.dispatchEvent(new CustomEvent('supply-builder:flavour', { detail: { variantId: bag.id } }));
        }
        if (this.live) this.live.textContent = bag.name + ': ' + next + (next === 1 ? ' bag' : ' bags');
        this.render();
        return;
      }

      const pick = event.target.closest('[data-bb-pick]');
      if (pick && this.contains(pick)) {
        const nextPlan = pick.dataset.bbPick === 'once' ? 'once' : 'sub';
        if (nextPlan === 'sub' && this.totals().subBlocked) return;
        this.plan = nextPlan;
        this.render();
        /* The control that was pressed disappears on switch; keep keyboard focus. */
        const target = this.querySelector(nextPlan === 'once' ? '.bb__back-link' : '.bb__once-link');
        if (pick.matches('.bb__once-link, .bb__back-link') && target) target.focus();
      }
    }

    /* Render -------------------------------------------------------------- */

    render() {
      const t = this.totals();
      if (t.subBlocked && this.plan === 'sub') this.plan = 'once';
      const isSub = this.plan === 'sub';
      const w = this.welcome;
      const subPay = w ? t.welcome : t.sub;
      const pay = isSub ? subPay : t.list;
      const saving = t.list - t.sub;
      const money = (c) => this.money(c);
      const set = (selector, text) => {
        const el = this.querySelector(selector);
        if (el) el.textContent = text;
      };

      this.classList.toggle('is-once', !isSub);
      this.classList.toggle('is-empty', !t.has);

      /* "You're saving $X" / "Subscribe to save $X" */
      set('[data-bb-saving]', !t.has || saving <= 0 ? '' : (isSub ? 'You’re saving ' : 'Subscribe to save ') + money(saving));

      /* Progress bar: nodes reached, and the fill from the first node to the last. */
      const nodes = this.data.nodes || [];
      const first = nodes[0] || 0;
      const last = nodes[nodes.length - 1] || 1;
      this.querySelectorAll('[data-bb-node]').forEach((node) => {
        node.classList.toggle('is-done', isSub && t.servings >= Number(node.dataset.bbNode));
      });
      const fill = isSub ? Math.min(Math.max((t.servings - first) / (last - first), 0), 1) : 0;
      const fillEl = this.querySelector('[data-bb-fill]');
      if (fillEl) fillEl.style.width = (fill * 100).toFixed(1) + '%';

      /* Flavours header: "$1.75/serving · 45 servings" */
      set('[data-bb-per]', t.has ? money(Math.round(pay / t.servings)) + '/serving ·' : '');
      set('[data-bb-servings]', t.has ? t.servings + ' servings' : '');

      this.bags.forEach((bag) => {
        if (!bag.row) return;
        bag.row.dataset.qty = String(bag.qty);
        const count = bag.row.querySelector('[data-bb-count]');
        if (count) count.textContent = String(bag.qty);
        const minus = bag.row.querySelector('[data-bb-step="-1"]');
        const plus = bag.row.querySelector('[data-bb-step="1"]');
        if (minus) minus.disabled = bag.qty === 0;
        if (plus) plus.disabled = bag.qty >= bag.max;
      });

      /* Subscribe card */
      const subCard = this.querySelector('[data-bb-sub]');
      if (subCard) subCard.classList.toggle('is-selected', isSub);
      this.querySelectorAll('[data-bb-pick="sub"].bb__sub-head').forEach((b) => b.setAttribute('aria-pressed', String(isSub)));
      const was = this.querySelector('[data-bb-was]');
      if (was) {
        was.textContent = money(t.list);
        was.hidden = !t.has;
      }
      set('[data-bb-now]', t.has ? (t.subBlocked ? '—' : money(subPay)) : money(0));
      set('[data-bb-saveline]', t.has ? 'Save ' + t.label + '% on every order' : 'Save ' + (this.data.labels || [9])[0] + '% or more on every order');
      const ship = this.querySelector('[data-bb-shipline]');
      if (ship) ship.hidden = !(t.has && subPay >= this.data.shipThreshold);

      /* W4: first order, every order after, and the next-tier hint */
      if (w) {
        set('[data-bb-w-firstpct]', t.welcomeLabel + '% off');
        set('[data-bb-w-first]', money(t.has ? t.welcome : 0));
        set('[data-bb-w-afterpct]', t.label + '% off');
        set('[data-bb-w-after]', money(t.has ? t.sub : 0));
        const hint = this.querySelector('[data-bb-w-hint]');
        if (hint) {
          hint.hidden = !(t.has && t.nextLabel);
          hint.textContent = t.nextLabel ? 'Add 1 more bag: every order after drops to ' + t.nextLabel + '% off' : '';
        }
        this.querySelectorAll('[data-bb-w-pct]').forEach((el) => {
          el.textContent = t.welcomeLabel + '%';
        });
      }

      /* Button, renewal line, "Try it once" */
      const cta = this.querySelector('[data-bb-cta]');
      /* v2: "Subscribe" while subscribing (both boxes with the card; the welcome box
         always), "Add to cart" for one-time. The evergreen alt keeps "Add to cart". */
      set('[data-bb-cta-label]', !t.has ? 'Add a bag to start' : isSub && (this.cards || w) ? 'Subscribe' : 'Add to cart');

      const onceCard = this.querySelector('.bb__once-card');
      if (onceCard) {
        onceCard.classList.toggle('is-selected', !isSub);
        onceCard.setAttribute('aria-pressed', String(!isSub));
      }
      set('[data-bb-once-card-price]', money(t.list));
      if (cta) {
        cta.disabled = !t.has;
        cta.classList.toggle('is-dim', !t.has);
      }
      const renew = this.querySelector('[data-bb-renew]');
      if (renew) renew.hidden = !isSub;

      const onceLink = this.querySelector('.bb__once-link');
      const back = this.querySelector('[data-bb-back]');
      if (onceLink) onceLink.hidden = !isSub;
      const onceNote = this.querySelector('[data-bb-once-note]');
      if (onceNote) onceNote.hidden = !isSub;
      if (back) back.hidden = isSub;
      set('[data-bb-once-price]', money(t.list));
      set('[data-bb-once-ship]', t.has && t.list < this.data.shipThreshold ? ' + ' + money(this.data.shipFee) + ' shipping' : '');
      set('[data-bb-pct]', (w ? t.welcomeLabel : t.label) + '%');

      if (this.hiddenInput) this.hiddenInput.value = isSub ? this.planId : '';
      this.totalsCache = t;
    }

    /* Tooltip: tap/click toggles, anywhere else or Escape closes --------- */

    /* Tooltip: hover shows it where there is a mouse (a click pins it open); a tap
       toggles it on touch. A press anywhere outside it, or Escape, closes it -
       pointerdown, not click: iOS Safari fires no click for a tap on plain content. */
    bindTips() {
      this.tips = Array.from(this.querySelectorAll('.bb__info'));
      if (!this.tips.length) return;
      const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.tips.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          if (canHover.matches) {
            button.dataset.pinned = 'true';
            this.toggleTip(button, true);
          } else {
            this.toggleTip(button, button.getAttribute('aria-expanded') !== 'true');
          }
        });
        const wrap = button.closest('.bb__tip-wrap');
        if (!wrap) return;
        wrap.addEventListener('mouseenter', () => {
          if (canHover.matches) this.toggleTip(button, true);
        });
        wrap.addEventListener('mouseleave', () => {
          if (canHover.matches && !button.dataset.pinned) this.toggleTip(button, false);
        });
      });
      this.onDocDown = (event) => {
        if (event.target.closest && event.target.closest('.bb__tip-wrap')) return;
        this.tips.forEach((b) => this.toggleTip(b, false));
      };
      this.onKeydown = (event) => {
        if (event.key === 'Escape') this.tips.forEach((b) => this.toggleTip(b, false));
      };
      document.addEventListener('pointerdown', this.onDocDown);
      document.addEventListener('keydown', this.onKeydown);
    }

    toggleTip(button, open) {
      const tip = document.getElementById(button.getAttribute('aria-controls'));
      if (!tip) return;
      if (open) this.tips.forEach((other) => other !== button && this.toggleTip(other, false));
      if (!open) delete button.dataset.pinned;
      button.setAttribute('aria-expanded', String(open));
      tip.hidden = !open;
    }

    /* Add to cart --------------------------------------------------------- */

    bindSubmit() {
      const form = document.getElementById(this.dataset.formId);
      this.form = form;
      this.productForm = form ? form.closest('product-form') : null;
      this.submitHost = this.productForm || form;
      if (!this.submitHost) return;
      this.onSubmit = (event) => {
        if (event.target !== form) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.addToCart();
      };
      this.submitHost.addEventListener('submit', this.onSubmit, true);
    }

    lines() {
      const isSub = this.plan === 'sub';
      return this.bags
        .filter((bag) => bag.qty > 0)
        .map((bag) => {
          const item = { id: bag.id, quantity: bag.qty };
          if (isSub && this.planId) item.selling_plan = Number(this.planId);
          return item;
        });
    }

    addToCart() {
      const button = this.querySelector('[data-bb-cta]');
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      const items = this.lines();
      if (!items.length) return;

      this.showError(false);
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');

      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const body = { items };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        cart.setActiveElement(document.activeElement);
      }

      const config = fetchConfig('json');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify(body);

      const code = this.data.welcomeCode;
      const ready = this.dataset.applyWelcome === 'true' && this.plan === 'sub' && code ? this.applyCode(code) : Promise.resolve();

      ready
        .then(() => fetch(window.routes.cart_add_url, config))
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.showError(response.description || response.message);
            return;
          }
          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'smk-buy-box', cartData: response });
          }
          cart.classList.remove('is-empty');
          cart.renderContents(response);
        })
        .catch((error) => {
          console.error(error);
          this.showError(window.variantStrings?.unavailable || 'Something went wrong. Please try again.');
        })
        .finally(() => {
          button.classList.remove('is-loading');
          button.removeAttribute('aria-disabled');
        });
    }

    /* Adds the code to whatever codes the cart already holds (/cart/update.js replaces the list). */
    async applyCode(code) {
      const json = { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      const cart = await fetch(cartUrl + '.js', json).then((r) => r.json());
      const codes = (cart.discount_codes || []).map((entry) => String(entry.code || '')).filter(Boolean);
      if (codes.some((entry) => entry.toUpperCase() === String(code).toUpperCase())) return;
      codes.push(code);
      const updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update';
      await fetch(updateUrl + '.js', Object.assign({ method: 'POST', body: JSON.stringify({ discount: codes.join(',') }) }, json));
    }

    showError(message) {
      const host = this.productForm || this.form;
      const wrapper = host && host.querySelector('.product-form__error-message-wrapper');
      if (!wrapper) return;
      wrapper.toggleAttribute('hidden', !message);
      const text = wrapper.querySelector('.product-form__error-message');
      if (text && message) text.textContent = message;
    }

    /* Money: the shop's own format, as Liquid's `| money` would print it. */
    money(cents) {
      const format = this.dataset.moneyFormat || '${{amount}}';
      const token = format.match(/\{\{\s*(\w+)\s*\}\}/);
      const value = Number(cents) || 0;
      const group = (places, thousands, decimal) => {
        const [whole, fraction] = (value / 100).toFixed(places).split('.');
        return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
      };
      if (!token) return group(2, ',', '.');
      const styles = {
        amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','],
        amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_apostrophe_separator: [2, "'", '.'],
        amount_with_space_separator: [2, ' ', ','],
      };
      const [places, thousands, decimal] = styles[token[1]] || [2, ',', '.'];
      return format.replace(token[0], group(places, thousands, decimal));
    }
  }

  customElements.define('smk-buy-box', SmkBuyBox);
}
